import { useAuth } from '../../App'
import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { headerAddHidden, resolveRouteTitle } from '../../lib/routeTitles'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import PostAddOrigin from '../collection/PostAddOrigin'
import BottomNav from './BottomNav'
import PageRecovery from './PageErrorBoundary'
import TopBar from './TopBar'
import InstallPrompt from '../pwa/InstallPrompt'
import WearSaveConfirmation from '../collection/WearSaveConfirmation'
import LayeringFeedbackPrompt from '../layering/LayeringFeedbackPrompt'
import { MobileChromeProvider } from './MobileChrome'
import { InstallGuideSheet } from '../pwa/InstallPrompt'
import { isStandalone, isInstallGuideSeen, markInstallGuideSeen } from '../../lib/pwaInstall'

export default function Layout() {
  const { user } = useAuth()
  const bottleOriginEnabled = useFeatureFlag('bottle_origin')
  const wearLogCleanupEnabled = useFeatureFlag('wear_log_tab_cleanup')
  const { pathname } = useLocation()
  const [headerAction, setHeaderAction] = useState(null)

  const [headerData, setHeaderData] = useState(null)
  const title = wearLogCleanupEnabled && pathname === '/log'
    ? 'Wear Log'
    : resolveRouteTitle(pathname, headerData?.pathname === pathname ? headerData : null)

  const [firstVisitBannerVisible, setFirstVisitBannerVisible] = useState(false)
  const [installGuideOpen, setInstallGuideOpen] = useState(false)

  const userId = user?.id
  useEffect(() => {
    // First-visit guide banner: once per signed-in account (the answer is
    // stored per user id), never inside the installed app.
    setFirstVisitBannerVisible(Boolean(userId) && !isStandalone() && !isInstallGuideSeen(userId))
  }, [userId])

  const dismissFirstVisitBanner = () => {
    markInstallGuideSeen(userId)
    setFirstVisitBannerVisible(false)
  }

  const showInstallGuide = () => {
    markInstallGuideSeen(userId)
    setFirstVisitBannerVisible(false)
    setInstallGuideOpen(true)
  }

  return (
    <MobileChromeProvider routeKey={pathname}>
      <div className="flex min-h-screen flex-col bg-charcoal-900">
        <TopBar action={headerAction} title={title} signedIn={Boolean(user)} meta={pathname === '/' ? headerData?.meta : null} showCollectionAction={!headerAddHidden(pathname)} />
        <main className="app-main flex-1">
          {/* Normal flow, never fixed: a fixed strip above the bottom nav sat on
              top of page controls (and of sheets' own buttons) until dismissed. */}
          {firstVisitBannerVisible && (
            <div className="px-4 pt-3" data-install-guide-banner>
              <div className="mx-auto flex max-w-md items-center justify-between gap-3 rounded-lg border border-gold/20 bg-charcoal-800 px-4 py-2">
                <p className="flex-1 text-sm text-cream/80">Want the app on your phone?</p>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={dismissFirstVisitBanner}
                    data-install-guide-dismiss
                    className="min-h-11 px-3 text-xs text-cream/60 transition-colors hover:text-cream/90"
                  >
                    Not now
                  </button>
                  <button
                    type="button"
                    onClick={showInstallGuide}
                    data-install-guide-open
                    className="min-h-11 px-3 text-xs font-semibold text-gold transition-colors hover:text-gold/80"
                  >
                    Show me how
                  </button>
                </div>
              </div>
            </div>
          )}
          <PageRecovery><Outlet context={{ setHeaderAction, setHeaderData }} /></PageRecovery>
        </main>
        <WearSaveConfirmation />
        {user && bottleOriginEnabled && <PostAddOrigin key={user.id} />}
        <LayeringFeedbackPrompt />
        <InstallPrompt />
        <InstallGuideSheet open={installGuideOpen} onClose={() => setInstallGuideOpen(false)} />
        <BottomNav signedIn={Boolean(user)} />
      </div>
    </MobileChromeProvider>
  )
}
